import { useConfirm } from "./ConfirmProvider";
import { useState } from "react";
import toast from "react-hot-toast";
import { relancesAPI } from "../services/api";
import { formatRelanceDate } from "./relanceDates";
import RelanceEmailPreview from "./RelanceEmailPreview";
import PenaliteBreakdown from "./PenaliteBreakdown";
import PenaliteControls from "./PenaliteControls";
import InvoiceDeliveryStatus from "./InvoiceDeliveryStatus";

const EVENT_LABELS = {
  envoyee: "Envoyée", echec: "Échec", en_cours: "À vérifier",
  incertain: "À vérifier", preparation: "Préparation interrompue",
};

const RelanceInfo = ({ facture, onChanged }) => {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const plan = facture.relances;
  const perform = async (operation, success) => {
    setBusy(true);
    try {
      await operation();
      toast.success(success);
      await onChanged();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Impossible de modifier les relances");
    } finally { setBusy(false); }
  };
  const togglePause = async () => {
    if (!(await confirm({ title: facture.relances_en_pause ? "Reprendre les relances ?" : "Mettre les relances en pause ?",
      description: facture.relances_en_pause ? "Les relances automatiques reprendront selon le calendrier applicable à cette facture." : "Les relances automatiques seront suspendues pour cette facture. Vous pourrez les reprendre depuis cette fiche.",
      confirmLabel: facture.relances_en_pause ? "Reprendre les relances" : "Mettre en pause", details: [["Facture", facture.numero], ["Client", facture.client_nom]] }))) return;
    perform(() => relancesAPI.pause(facture.id, !facture.relances_en_pause), facture.relances_en_pause ? "Relances reprises" : "Relances en pause");
  };
  const confirmSent = async () => {
    if (!await confirm(`Confirmer que la facture ${facture.numero} a déjà été transmise au client ? Aucun email ne sera envoyé par cette confirmation.`)) return;
    perform(() => relancesAPI.confirmInvoiceSent(facture.id), "Envoi initial confirmé");
  };
  const verify = async (event, sent) => {
    const message = sent
      ? "Confirmer que cette relance a bien été envoyée après vérification ?"
      : "Confirmer que cette relance n'a pas été envoyée ? Un nouvel essai sera possible au prochain contrôle.";
    if (!await confirm(message)) return;
    perform(() => relancesAPI.verifyDelivery(facture.id, event.id, sent), "Vérification enregistrée");
  };
  return (
    <div className="text-sm space-y-3">
      <p className="text-gray-700">Échéance : {formatRelanceDate(facture.date_echeance)}</p>
      <InvoiceDeliveryStatus facture={facture} onChanged={onChanged} />
      {facture.statut === "non_paye" && plan && <>
        <RelanceEmailPreview factureId={facture.id} numero={facture.numero} />
        {plan.jours_retard > 0 && <p className="text-amber-700 font-medium">{plan.jours_retard} jour(s) de retard</p>}
        <p className={plan.a_traiter ? "text-red-700 font-medium" : "text-gray-600"}>
          {plan.motif || (plan.a_envoyer ? "Relance due, envoi au prochain contrôle" :
            `Prochaine relance : ${formatRelanceDate(plan.prochaine_relance, true)}`)}
        </p>
        <button type="button" disabled={busy}
          className="text-primary-700 hover:underline disabled:opacity-50"
          onClick={togglePause}>
          {facture.relances_en_pause ? "Reprendre pour cette facture" : "Mettre en pause"}
        </button>
        {plan.etat === "non_envoyee" && !facture.envoi_initial?.a_verifier && facture.envoi_initial?.etat !== "en_attente" && <button type="button" disabled={busy} onClick={confirmSent}
          className="block text-primary-700 hover:underline disabled:opacity-50">Confirmer la facture déjà envoyée</button>}
      </>}
      {facture.derniere_relance && <p className="text-gray-500">
        Dernière relance : {formatRelanceDate(facture.derniere_relance)} ({facture.relances_count || 0} envoi(s))
      </p>}
      {facture.statut === "non_paye" && <>
        <PenaliteBreakdown calcul={facture.penalites} compact />
        <PenaliteControls facture={facture} onChanged={onChanged} />
      </>}
      {!!plan?.historique?.length && <details className="text-gray-600">
        <summary className="cursor-pointer">Historique des relances</summary>
        <ul className="space-y-3 mt-2">
          {[...plan.historique].reverse().map((event) => <li key={event.id}>
            <p>{formatRelanceDate(event.date, true)} : {EVENT_LABELS[event.statut] || event.statut}</p>
            <p>{event.mode === "automatique" ? `Automatique (${event.palier < 0 ? "J" : "J+"}${event.palier})` : "Manuelle"}</p>
            {event.erreur && <p className="text-red-700">{event.erreur}</p>}
            {event.penalites?.reclamables && <PenaliteBreakdown calcul={event.penalites} compact />}
            {["en_cours", "incertain"].includes(event.statut) && <div className="flex flex-wrap gap-2 mt-1">
              <button type="button" disabled={busy} className="text-primary-700 hover:underline" onClick={() => verify(event, true)}>Envoi confirmé</button>
              <button type="button" disabled={busy} className="text-primary-700 hover:underline" onClick={() => verify(event, false)}>Non envoyée</button>
            </div>}
          </li>)}
        </ul>
      </details>}
    </div>
  );
};

export default RelanceInfo;
